import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './TeacherProctoringDashboard.css'
import LoadingScreen from '../components/LoadingScreen'

const TeacherProctoringDashboard = () => {
  const [user, setUser] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedAttempt, setSelectedAttempt] = useState(null)
  const [attemptDetails, setAttemptDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const history = useHistory()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    if (userData.role !== 'teacher') {
      history.push('/login')
      return
    }

    setUser(userData)
    fetchDashboardData()
  }, [history])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/teacher/proctoring-dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDashboardData(data)
      } else {
        setError('Failed to load proctoring dashboard data')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const fetchAttemptDetails = async (attemptId) => {
    setLoadingDetails(true)
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`/api/teacher/attempt-violations/${attemptId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAttemptDetails(data)
        setSelectedAttempt(attemptId)
      } else {
        setError('Failed to load attempt details')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    history.push('/login')
  }

  const getViolationTypeLabel = (type) => {
    const labels = {
      'tab_switch': 'Tab Switch',
      'focus_loss': 'Focus Loss',
      'fullscreen_exit': 'Fullscreen Exit',
      'no_face_detected': 'No Face',
      'multiple_faces': 'Multiple Faces',
      'face_mismatch': 'Face Mismatch',
      'face_not_looking': 'Not Looking',
      'liveness_failed': 'Liveness Failed',
      'page_refresh': 'Page Refresh',
      'hard_refresh_attempt': 'Hard Refresh',
      'service_unavailable': 'Service Down',
      'detection_error': 'Detection Error',
      'network_error': 'Network Error'
    }
    return labels[type] || type
  }

  const getSeverityColor = (severity) => {
    return severity === 'high' ? '#e53e3e' : '#ed8936'
  }

  const getRiskScoreColor = (score) => {
    if (score >= 0.7) return '#e53e3e'
    if (score >= 0.4) return '#ed8936'
    if (score >= 0.2) return '#ecc94b'
    return '#48bb78'
  }

  const getRiskLabel = (score) => {
    if (score >= 0.7) return 'High Risk'
    if (score >= 0.4) return 'Medium Risk'
    if (score >= 0.2) return 'Low Risk'
    return 'No Risk'
  }

  if (loading) {
    return <LoadingScreen message="Loading Proctoring Dashboard" />
  }

  if (error) {
    return (
      <div className="proctoring-dashboard-container">
        <div className="error">{error}</div>
        <button onClick={() => history.push('/teacher-dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </div>
    )
  }

  return (
    <div className="proctoring-dashboard-container">
      <header className="proctoring-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Proctoring Dashboard</h1>
            <p className="subtitle">Monitor test integrity and violations</p>
          </div>
          <div className="header-actions">
            <button onClick={() => history.push('/teacher-dashboard')} className="back-button">
              Back to Dashboard
            </button>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="proctoring-main">
        {/* Summary Stats */}
        <section className="stats-section">
          <h2>Overview</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{dashboardData?.summary?.total_attempts || 0}</h3>
                <p>Total Attempts</p>
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">⚠️</div>
              <div className="stat-content">
                <h3>{dashboardData?.summary?.flagged_attempts || 0}</h3>
                <p>Flagged Attempts</p>
              </div>
            </div>

            <div className="stat-card danger">
              <div className="stat-icon">🚫</div>
              <div className="stat-content">
                <h3>{dashboardData?.summary?.auto_submitted || 0}</h3>
                <p>Auto-Submitted</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <h3>{dashboardData?.summary?.total_violations || 0}</h3>
                <p>Total Violations</p>
              </div>
            </div>
          </div>
        </section>

        {/* Violation Breakdown */}
        <section className="breakdown-section">
          <h2>Violation Breakdown</h2>
          <div className="violation-breakdown">
            {dashboardData?.violation_breakdown && Object.keys(dashboardData.violation_breakdown).length > 0 ? (
              <div className="breakdown-grid">
                {Object.entries(dashboardData.violation_breakdown).map(([type, data]) => (
                  <div key={type} className="breakdown-item">
                    <div className="breakdown-type">{getViolationTypeLabel(type)}</div>
                    <div className="breakdown-count">{data.count}</div>
                    <div className="breakdown-severity">
                      <span className="severity-badge high">{data.severity_high} High</span>
                      <span className="severity-badge medium">{data.severity_medium} Medium</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No violations recorded</p>
            )}
          </div>
        </section>

        <div className="dashboard-columns">
          {/* Flagged Attempts List */}
          <section className="flagged-section">
            <h2>Flagged Attempts</h2>
            <div className="flagged-list">
              {dashboardData?.flagged_attempts?.length > 0 ? (
                dashboardData.flagged_attempts.map((attempt) => (
                  <div
                    key={attempt.attempt_id}
                    className={`flagged-item ${selectedAttempt === attempt.attempt_id ? 'selected' : ''}`}
                    onClick={() => fetchAttemptDetails(attempt.attempt_id)}
                  >
                    <div className="flagged-header">
                      <span className="student-name">{attempt.student_name}</span>
                      <span className={`status-badge status-${attempt.status}`}>
                        {attempt.status}
                      </span>
                    </div>
                    <div className="flagged-info">
                      <p className="test-name">{attempt.test_title}</p>
                      <div className="violation-summary">
                        <span className="violation-count">{attempt.violation_count} violations</span>
                        {attempt.score !== null && (
                          <span className="score">Score: {attempt.percentage}%</span>
                        )}
                      </div>
                    </div>
                    <div className="flagged-time">
                      {attempt.started_at && new Date(attempt.started_at).toLocaleString()}
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-data">No flagged attempts</p>
              )}
            </div>
          </section>

          {/* Attempt Details Panel */}
          <section className="details-section">
            <h2>Attempt Details</h2>
            {loadingDetails ? (
              <div className="loading-details">Loading...</div>
            ) : attemptDetails ? (
              <div className="attempt-details">
                <div className="details-header">
                  <h3>{attemptDetails.student?.name}</h3>
                  <p>{attemptDetails.student?.email}</p>
                </div>

                <div className="details-info">
                  <div className="info-row">
                    <span className="label">Test:</span>
                    <span className="value">{attemptDetails.test_title}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Status:</span>
                    <span className={`status-badge status-${attemptDetails.attempt_info?.status}`}>
                      {attemptDetails.attempt_info?.status}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Score:</span>
                    <span className="value">
                      {attemptDetails.attempt_info?.score} / {attemptDetails.attempt_info?.total_points}
                      ({attemptDetails.attempt_info?.percentage?.toFixed(1)}%)
                    </span>
                  </div>
                </div>

                {/* Proctoring Stats */}
                <div className="proctoring-stats">
                  <h4>Proctoring Statistics</h4>
                  <div className="stats-bars">
                    <div className="stat-bar">
                      <span className="bar-label">Face Verification</span>
                      <div className="bar-container">
                        <div
                          className="bar-fill verification"
                          style={{ width: `${(attemptDetails.proctoring_stats?.verification_rate || 0) * 100}%` }}
                        />
                      </div>
                      <span className="bar-value">
                        {((attemptDetails.proctoring_stats?.verification_rate || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="stat-bar">
                      <span className="bar-label">Attention Rate</span>
                      <div className="bar-container">
                        <div
                          className="bar-fill attention"
                          style={{ width: `${(attemptDetails.proctoring_stats?.attention_rate || 0) * 100}%` }}
                        />
                      </div>
                      <span className="bar-value">
                        {((attemptDetails.proctoring_stats?.attention_rate || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="stat-bar">
                      <span className="bar-label">Liveness Rate</span>
                      <div className="bar-container">
                        <div
                          className="bar-fill liveness"
                          style={{ width: `${(attemptDetails.proctoring_stats?.liveness_rate || 0) * 100}%` }}
                        />
                      </div>
                      <span className="bar-value">
                        {((attemptDetails.proctoring_stats?.liveness_rate || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  <div className="risk-score">
                    <span className="risk-label">Risk Score:</span>
                    <span
                      className="risk-value"
                      style={{ color: getRiskScoreColor(attemptDetails.proctoring_stats?.risk_score || 0) }}
                    >
                      {getRiskLabel(attemptDetails.proctoring_stats?.risk_score || 0)}
                      ({((attemptDetails.proctoring_stats?.risk_score || 0) * 100).toFixed(0)}%)
                    </span>
                  </div>

                  <div className="frames-info">
                    Total frames analyzed: {attemptDetails.proctoring_stats?.total_frames || 0}
                  </div>
                </div>

                {/* Violation Timeline */}
                <div className="violation-timeline">
                  <h4>Violation Timeline ({attemptDetails.violations?.total_count || 0} total)</h4>
                  <div className="timeline-list">
                    {attemptDetails.violations?.timeline?.length > 0 ? (
                      attemptDetails.violations.timeline.map((violation, index) => (
                        <div key={index} className="timeline-item">
                          <div
                            className="timeline-marker"
                            style={{ backgroundColor: getSeverityColor(violation.severity) }}
                          />
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <span className="violation-type">{getViolationTypeLabel(violation.type)}</span>
                              <span
                                className="violation-severity"
                                style={{ color: getSeverityColor(violation.severity) }}
                              >
                                {violation.severity}
                              </span>
                            </div>
                            <p className="violation-message">{violation.message}</p>
                            <span className="violation-time">
                              {violation.timestamp && new Date(violation.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">No violations recorded</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-selection">
                <p>Select an attempt to view details</p>
              </div>
            )}
          </section>
        </div>

        {/* Recent Violations */}
        <section className="recent-section">
          <h2>Recent Violations</h2>
          <div className="recent-violations-list">
            {dashboardData?.recent_violations?.length > 0 ? (
              <table className="violations-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Test</th>
                    <th>Violation</th>
                    <th>Severity</th>
                    <th>Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.recent_violations.map((item, index) => (
                    <tr
                      key={index}
                      className="violation-row"
                      onClick={() => fetchAttemptDetails(item.attempt_id)}
                    >
                      <td>{item.student_name}</td>
                      <td>{item.test_title}</td>
                      <td>{getViolationTypeLabel(item.violation?.type)}</td>
                      <td>
                        <span
                          className="severity-indicator"
                          style={{ color: getSeverityColor(item.violation?.severity) }}
                        >
                          {item.violation?.severity}
                        </span>
                      </td>
                      <td>
                        {item.violation?.timestamp && new Date(item.violation.timestamp).toLocaleString()}
                      </td>
                      <td>
                        <span className={`status-badge status-${item.attempt_status}`}>
                          {item.attempt_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No recent violations</p>
            )}
          </div>
        </section>
      </main>
    </div>
  )
}

export default TeacherProctoringDashboard
